<template>
  <div>
    <div class="scrim" :data-open="open ? '' : null" @click="onScrimClick"></div>
    <div
      ref="sheet"
      class="sheet"
      :class="sheetClass"
      :data-open="open ? '' : null"
      role="dialog"
      aria-modal="true"
      :aria-label="title || null"
      tabindex="-1"
      @keydown.esc="onEscape"
    >
      <div class="grab"></div>
      <slot></slot>
    </div>
  </div>
</template>

<script>
/**
 * The sliding bottom sheet used for the permission gate, the photo guide and support.
 *
 * `persistent` marks a sheet the flow physically cannot continue past — the camera gate.
 * Those ignore scrim taps and Escape; the only way out is granting access, which closes
 * the sheet programmatically.
 *
 * (The prototype also neutralised the browser Back button for persistent sheets by
 * parking a spare history entry. That is not carried over: this is a routed app, so Back
 * means "previous screen", which is a legitimate thing for the user to want. The gate is
 * still not dismissible — it simply is not a history entry to begin with.)
 */
export default {
  name: "BottomSheet",

  props: {
    open: { type: Boolean, default: false },
    /** Cannot be dismissed by the user; only by the parent setting `open` to false. */
    persistent: { type: Boolean, default: false },
    title: { type: String, default: "" },
    /** Extra class for sheets with bespoke layout (the scrolling photo guide). */
    sheetClass: { type: String, default: "" },
  },

  watch: {
    open(isOpen) {
      if (isOpen) {
        this.previouslyFocused = document.activeElement;
        // Wait for the transition to start so the element is focusable.
        this.$nextTick(() => {
          if (this.$refs.sheet) this.$refs.sheet.focus();
        });
      } else if (this.previouslyFocused && this.previouslyFocused.focus) {
        this.previouslyFocused.focus();
        this.previouslyFocused = null;
      }
    },
  },

  created() {
    // Not reactive — it is a DOM node we hand back focus to, never something we render.
    this.previouslyFocused = null;
  },

  methods: {
    onScrimClick() {
      if (!this.persistent) this.$emit("close");
    },
    onEscape() {
      if (!this.persistent) this.$emit("close");
    },
  },
};
</script>

<style scoped>
.scrim {
  position: absolute;
  inset: 0;
  z-index: 20;
  background: rgba(9, 10, 13, 0.5);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s, visibility 0.3s;
}

.scrim[data-open] {
  opacity: 1;
  visibility: visible;
}

.sheet {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 21;
  background: var(--card);
  border-top-left-radius: 26px;
  border-top-right-radius: 26px;
  border-top: 1px solid var(--border);
  box-shadow: var(--sheet-shadow);
  transform: translateY(101%);
  visibility: hidden;
  pointer-events: none;
  transition: transform 0.36s cubic-bezier(0.32, 0.72, 0, 1), visibility 0.36s;
  padding: 10px 20px calc(22px + env(safe-area-inset-bottom));
  max-height: 90%;
  overflow-y: auto;
}

.sheet[data-open] {
  transform: none;
  visibility: visible;
  pointer-events: auto;
}

.sheet:focus {
  outline: none;
}

.grab {
  width: 42px;
  height: 5px;
  border-radius: 999px;
  background: var(--border);
  margin: 4px auto 16px;
}

@media (prefers-reduced-motion: reduce) {
  .scrim,
  .sheet {
    transition: none;
  }
}
</style>

<style>
/* Sheet content is provided by the parent through the default slot, so these structural
   rules cannot be scoped to this component. They are namespaced under `.sheet` and only
   describe the sheet's own anatomy. */
.sheet .sh-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  margin-bottom: 16px;
}

.sheet h2,
.sheet p {
  text-align: center;
}

.sheet .sh-foot {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sheet ol.steps {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 13px;
}

.sheet ol.steps li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.sheet ol.steps .n {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: var(--primary-soft);
  color: var(--primary);
  font-weight: 700;
  font-size: 14px;
  display: grid;
  place-items: center;
}

.sheet ol.steps .t {
  font-size: 14.5px;
  color: var(--foreground);
  padding-top: 2px;
  text-align: right;
  line-height: 1.75;
}

.sheet ol.steps .t .mini {
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: var(--muted);
  color: var(--muted-foreground);
  vertical-align: -6px;
  margin: 0 2px;
}

.sheet ol.steps .t .mini svg {
  width: 14px;
  height: 14px;
}
</style>
